export interface ModalHandle {
  overlay: HTMLElement;
  modal: HTMLElement;
  body: HTMLElement;
  onClose: (() => void) | null;
  onOpen: ((handle: ModalHandle) => void) | null;
  closeOnEscape: boolean;
  isOpen: boolean;
  openingFrame: number | null;
  closingTimer: number | null;
  closing: boolean;
  closeSettled: boolean;
}

export interface ModalLifecycleScheduler {
  requestFrame(callback: FrameRequestCallback): number;
  cancelFrame(id: number): void;
  setTimer(callback: () => void, delayMs: number): number;
  clearTimer(id: number): void;
  addKeydown(listener: (event: KeyboardEvent) => void): void;
  removeKeydown(listener: (event: KeyboardEvent) => void): void;
}

const browserScheduler: ModalLifecycleScheduler = {
  requestFrame: (callback) => window.requestAnimationFrame(callback),
  cancelFrame: (id) => window.cancelAnimationFrame(id),
  setTimer: (callback, delayMs) => window.setTimeout(callback, delayMs),
  clearTimer: (id) => window.clearTimeout(id),
  addKeydown: (listener) => window.addEventListener('keydown', listener),
  removeKeydown: (listener) => window.removeEventListener('keydown', listener),
};

/** Owns the modal stack and every page-lifetime scheduling resource. */
export class ModalLifecycleOwner {
  readonly stack: ModalHandle[] = [];

  private readonly scheduler: ModalLifecycleScheduler;
  private readonly allHandles = new Set<ModalHandle>();
  private listening = false;
  private disposed = false;

  constructor(scheduler: ModalLifecycleScheduler = browserScheduler) {
    this.scheduler = scheduler;
  }

  get active(): ModalHandle | null {
    return this.stack.at(-1) ?? null;
  }

  register(handle: ModalHandle): void {
    if (this.disposed) throw new Error('ModalLifecycleOwner is disposed');
    this.allHandles.add(handle);
    this.stack.push(handle);
    this.ensureEscapeListener();
    handle.openingFrame = this.scheduler.requestFrame(() => {
      handle.openingFrame = null;
      if (handle.closing || handle.closeSettled || this.disposed) return;
      handle.overlay.classList.add('visible');
      handle.isOpen = true;
      handle.onOpen?.(handle);
    });
  }

  close(handle: ModalHandle | null | undefined, immediate = false): boolean {
    if (!handle || handle.closeSettled) return false;
    if (handle.closing) {
      if (immediate) this.finishClose(handle);
      return immediate;
    }
    handle.closing = true;
    handle.isOpen = false;

    if (handle.openingFrame !== null) {
      this.scheduler.cancelFrame(handle.openingFrame);
      handle.openingFrame = null;
    }

    const index = this.stack.indexOf(handle);
    if (index >= 0) this.stack.splice(index, 1);
    this.releaseEscapeListenerIfIdle();

    handle.overlay.classList.remove('visible');
    handle.overlay.style.pointerEvents = 'none';
    if (immediate) {
      this.finishClose(handle);
    } else {
      handle.closingTimer = this.scheduler.setTimer(() => this.finishClose(handle), 300);
    }
    return true;
  }

  closeAll(immediate = false): void {
    for (const handle of [...this.stack]) this.close(handle, immediate);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const handle of [...this.allHandles]) this.close(handle, true);
    this.releaseEscapeListener();
  }

  private readonly onKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    const top = this.active;
    if (!top || !top.closeOnEscape) return;
    this.close(top);
  };

  private ensureEscapeListener(): void {
    if (this.listening) return;
    this.scheduler.addKeydown(this.onKeydown);
    this.listening = true;
  }

  private releaseEscapeListenerIfIdle(): void {
    if (this.stack.length === 0) this.releaseEscapeListener();
  }

  private releaseEscapeListener(): void {
    if (!this.listening) return;
    this.scheduler.removeKeydown(this.onKeydown);
    this.listening = false;
  }

  private finishClose(handle: ModalHandle): void {
    if (handle.closeSettled) return;
    if (handle.closingTimer !== null) {
      this.scheduler.clearTimer(handle.closingTimer);
      handle.closingTimer = null;
    }
    handle.overlay.remove();
    handle.closeSettled = true;
    this.allHandles.delete(handle);
    handle.onClose?.();
  }
}
